/**
 * lib/api.js
 * API layer for the portfolio app.
 *
 * fetchPortfolioContent  — returns live content (falls back to static)
 * fetchSystemSnapshot    — returns server time + load metrics
 * submitContactRequest   — sends contact form to backend
 *
 * All functions are safe to call with no backend running —
 * they return sensible defaults so the UI never breaks.
 */

import { portfolioContent } from '../content/portfolioContent';

/* ── Base URL ──────────────────────────────────────────────
   Set VITE_API_URL in your .env for a real backend.
   Falls back to localhost:5000 in dev, no-op in production.
────────────────────────────────────────────────────────── */
// Never use localhost in production — guards against accidentally deploying
// a .env.local VITE_API_URL value to Vercel.
const _rawBase =
  import.meta.env?.VITE_API_URL ||
  (import.meta.env?.DEV ? 'http://localhost:5000' : '');
const API_BASE = import.meta.env?.PROD && _rawBase.includes('localhost')
  ? ''
  : _rawBase;

const API_RETRY_COOLDOWN_MS = 30000;
let apiRetryAfter = 0;

function isNetworkError(error) {
  if (!error) return false;
  if (error.name === 'TypeError') return true;

  const message = typeof error.message === 'string' ? error.message : '';
  return /Failed to fetch|NetworkError|Load failed|fetch/i.test(message);
}

/* ── Helpers ───────────────────────────────────────────── */
async function apiFetch(path, options = {}) {
  if (apiRetryAfter > Date.now()) {
    throw new Error('API temporarily unavailable');
  }

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Request failed: ${res.status}`);
    }

    apiRetryAfter = 0;
    return res.json();
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }

    if (isNetworkError(error)) {
      apiRetryAfter = Date.now() + API_RETRY_COOLDOWN_MS;
    }

    throw error;
  }
}

function buildLightingMode(hour) {
  if (hour >= 6  && hour < 12) return 'dawn-cyan';
  if (hour >= 12 && hour < 20) return 'neon-storm';
  return 'sapphire-night';
}

function buildLoadIntensity(hour) {
  // simulate "studio activity" based on time of day
  if (hour >= 9  && hour < 13) return 0.72;
  if (hour >= 14 && hour < 19) return 0.88;
  if (hour >= 20 && hour < 23) return 0.55;
  return 0.25;
}

/* ── fetchPortfolioContent ─────────────────────────────── */
export async function fetchPortfolioContent(signal) {
  if (!API_BASE) return portfolioContent;

  try {
    const data = await apiFetch('/api/portfolio', { signal });
    // Merge with static defaults so missing keys never crash the UI
    return { ...portfolioContent, ...data };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    // Network or backend error → use static content silently
    return portfolioContent;
  }
}

/* ── fetchSystemSnapshot ───────────────────────────────── */
export async function fetchSystemSnapshot() {
  const now     = new Date();
  const hour    = now.getHours();
  const minutes = now.getMinutes();
  const pad     = (n) => String(n).padStart(2, '0');

  const localDefault = {
    cpuPercent:     0,
    memoryPercent:  0,
    localTime:      `${pad(hour)}:${pad(minutes)}`,
    activeCores:    navigator.hardwareConcurrency ?? 4,
    lightingMode:   buildLightingMode(hour),
    loadIntensity:  buildLoadIntensity(hour),
    isPeakWorkHours: hour >= 9 && hour < 20,
  };

  if (!API_BASE) return localDefault;

  try {
    const data = await apiFetch('/api/system');
    return {
      ...localDefault,
      ...data,
      // Always keep localTime fresh from client if server doesn't send it
      localTime: data.localTime ?? localDefault.localTime,
    };
  } catch {
    return localDefault;
  }
}

/* ── submitContactRequest ──────────────────────────────── */
export async function submitContactRequest(form) {
  // Validate on client before sending
  if (!form.name?.trim())    throw new Error('Please enter your name.');
  if (!form.email?.trim())   throw new Error('Please enter your email.');
  if (!form.message?.trim()) throw new Error('Please enter a message.');

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  if (!emailOk) throw new Error('Please enter a valid email address.');

  if (!API_BASE) {
    // No backend — simulate success after a short delay
    await new Promise((r) => setTimeout(r, 900));
    return {
      ok: true,
      message:
        'Message received! We will get back to you within 24 hours. (Demo mode — no backend connected.)',
    };
  }

  return apiFetch('/api/contact', {
    method: 'POST',
    body: JSON.stringify({
      name:    form.name.trim(),
      email:   form.email.trim(),
      message: form.message.trim(),
    }),
  });
}
